import {
  cloneElement,
  MouseEvent,
  ReactElement,
  ReactNode,
  useMemo,
  useState,
} from "react";
import { ButtonProps, Menu, MenuProps, PopoverOrigin } from "@mui/material";
import groupBySelector from "lodash/groupBy";
import isEmpty from "lodash/isEmpty";
import { TRenderGroup } from "./ButtonMenu.types";
import Option from "./components/Option";
import OptionGroups from "./components/OptionGroups";

export type TMenuOption = {
  id: string;
  label: string;
  items?: TMenuOption[];
  onClick?: (selectedOption?: TMenuOption) => void;
  disabled?: boolean;
  shouldBeSeparated?: boolean;
  additionalInfo?: ReactNode;
  adornment?: ReactNode | null;
  groupName?: string;
};

export type TButtonMenuBaseProps = {
  value?: string | null;
  dense?: boolean;
  onClose?: () => void;
  onOpen?: () => void;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  onSelect?: (selectedOption: TMenuOption) => void;
  options: TMenuOption[];
  buttonProps?: ButtonProps;
  ariaLabel?: string;
  groupBy?: (option: TMenuOption) => string;
  renderGroup?: TRenderGroup;
};

export type TButtonMenuProps = Omit<
  MenuProps,
  "MenuListProps" | "onSelect" | "open"
> &
  TButtonMenuBaseProps;

const ButtonMenu = ({
  dense = true,
  onSelect,
  options,
  sx,
  value,
  onClose,
  onOpen,
  children,
  buttonProps,
  groupBy,
  renderGroup,
  ...props
}: TButtonMenuProps) => {
  const [menuElement, setMenuElement] = useState<HTMLElement | null>(null);

  const handleMenuOpen = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation(); // Prevents event bubbling when used within selectable table rows
    setMenuElement(event.currentTarget);
    onOpen?.();
  };

  const handleMenuClose = () => {
    if (menuElement) {
      setMenuElement(null);
    }

    onClose?.();
  };

  const button = cloneElement(children as ReactElement, {
    onClick: handleMenuOpen,
    ...buttonProps,
  });

  const isShowInset = useMemo(
    () => options.every(({ items }) => isEmpty(items)),
    [options],
  );

  const groupedOptions = useMemo(
    () => (groupBy ? groupBySelector(options, groupBy) : {}),
    [options, groupBy],
  );

  return (
    <>
      {button}
      {!isEmpty(options) && (
        <Menu
          onClose={handleMenuClose}
          sx={sx}
          open={!!menuElement}
          anchorEl={menuElement}
          tabIndex={0}
          {...props}
          slotProps={{
            list: {
              dense: dense,
            },
          }}
        >
          {groupBy ? (
            <OptionGroups
              groups={groupedOptions}
              groupBy={groupBy}
              renderGroup={renderGroup}
              dense={dense}
              onSelect={onSelect}
              value={value}
              onClose={handleMenuClose}
              isShowInset={isShowInset}
              {...props}
            />
          ) : (
            options.map((option) => (
              <Option
                key={option.id}
                dense={dense}
                onSelect={onSelect}
                value={value}
                onClose={handleMenuClose}
                isShowInset={isShowInset}
                shouldBeSeparated={
                  !!option.shouldBeSeparated && options.length > 1
                }
                option={option}
                {...props}
              />
            ))
          )}
        </Menu>
      )}
    </>
  );
};

export default ButtonMenu;
